// features/userBioListSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../auth/api";
import { alertError, alertSuccess } from "../components/Alert";

// ==============================
// Async thunk: Fetch user bio list
// ==============================
// export const fetchUserBioList = createAsyncThunk(
//   "userBioList/fetchUserBioList",
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await api.get("/panel/UserBio");
//       // Adjust based on API shape:
//       console.log(response.data)
//       return response.data; // or response.data.data if nested
//     } catch (error) {
//       return rejectWithValue(error.response?.data?.errors || error.message);
//     }
//   }
// );

export const fetchUserBioList = createAsyncThunk(
  "panelUser/fetchUserBioList",
  async ({ page = 1, search = "", fetchAll = false }, { rejectWithValue, getState }) => {
    try {
      const { userBioList } = getState();
      const { pageSize , cache} = userBioList;

        const cacheKey = `${page}_${search}_${pageSize}_${fetchAll}`;

      // ✅ cache check
      if (cache[cacheKey]) {
        return {
          data: cache[cacheKey],
          page,
          search,
          pageSize,
          fromCache: true,
        };
      }



      let url = `/panel/UserBio`;

      if (!fetchAll) {
        url += `?page=${page}&page_size=${pageSize}`;
      } else {
        url += `?page=1&page_size=10000`; // huge number to get all users
      }

      if (search) {
        url += `&search=${search}`;
      }

      const response = await api.get(url);

      return {
        data: response.data,
        page,
        search,
        pageSize,
        fromCache: false,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);




// ==============================
// Async thunk: Update user bio list
// ==============================
export const updateUserBioList = createAsyncThunk(
  "userBioList/updateUserBioList",
  async (updatedUserBioList, { rejectWithValue }) => {
    try {
      const response = await api.patch(
        "/panel/UserBio",
        updatedUserBioList
      );
      alertSuccess(
        response?.data?.success || "User Bio updated successfully!"
      );
      // Adjust based on API shape:
      return response.data; // or response.data.data if nested
    } catch (error) {
      alertError(error?.response?.data?.errors || "Error in updating user bio");
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  }
);

// ==============================
// Slice definition
// ==============================
const userBioListSlice = createSlice({
  name: "userBioList",
  initialState: {
    userBioList: { result: [], pagination: { total_pages: 1 } },
       search: "", // ✅ NEW
    currentPage: 1,
      pageSize: 10, // ✅ controlled here
    totalPages: 1,
    cache: {}, // store data per page
    status: "idle", // idle | loading | succeeded | failed
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
     setPage: (state, action) => {
      state.currentPage = action.payload;
    },

      setPageSize: (state, action) => {   // ✅ ADD THIS
      state.pageSize = action.payload;
      state.currentPage = 1;           // ✅ reset page
      state.cache = {};                // ✅ clear cache (VERY IMPORTANT)
    },
  
        // ✅ ADD THIS
      clearCache: (state) => {
        state.cache = {};
      }
  },
  extraReducers: (builder) => {
    builder
      // ---- Fetch User Bio List ----
      .addCase(fetchUserBioList.pending, (state) => {
        state.status = "loading";
      })


      .addCase(fetchUserBioList.fulfilled, (state, action) => {
         const { data, page, search, pageSize, fromCache } = action.payload;

         const cacheKey = `${page}_${search}_${pageSize}_${false}`;

        // ✅ Save in cache if not cached already
        if (!fromCache) {
          state.cache[cacheKey] = data;
        }
        state.userBioList = data;
         // ✅ Page handling
         state.currentPage = page;

      // ✅ IMPORTANT: Calculate total pages from count
        const totalCount = data?.pagination?.count || 0;
        state.totalPages = Math.ceil(totalCount / pageSize);

        state.search = search;
        state.pageSize = pageSize;
        state.status = "succeeded";
      })


      .addCase(fetchUserBioList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // ---- Update User Bio List ----
      .addCase(updateUserBioList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserBioList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userBioList = Array.isArray(action.payload)
          ? action.payload
          : [];
        state.error = null;
      })
      .addCase(updateUserBioList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError ,  setPage, setPageSize , clearCache} = userBioListSlice.actions;

export default userBioListSlice.reducer;
